import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { forwardRef } from 'react';

import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimUploadedFilesList } from './kim-uploaded-files-list';
import { KimUploadedFile } from '../hooks/use-kim-file-upload';
import { KIM_MAX_FILE_SIZE, KIM_MAX_NUMBER_OF_FILES } from '../utils/upload-constants';
import { DocMentionsInput, DocMentionsInputHandle } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

interface KimInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  uploadedFiles?: KimUploadedFile[];
  isUploading?: boolean;
  onFilesAdded?: (files: File[]) => void;
  onFileRemove?: (fileId: string) => void;
  autoFocus?: boolean;
}

export const KimInput = forwardRef<DocMentionsInputHandle, KimInputProps>(function KimInput(
  {
    value,
    onChange,
    onSubmit,
    placeholder,
    className,
    disabled,
    uploadedFiles = [],
    isUploading = false,
    onFilesAdded,
    onFileRemove,
    autoFocus,
  },
  ref,
) {
  const { matterId, subsetId } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<DocMentionsInputHandle>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    triggerMention: () => inputRef.current?.triggerMention(),
  }));

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFilesAdded?.(acceptedFiles);
      }
    },
    [onFilesAdded],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    maxSize: KIM_MAX_FILE_SIZE,
    maxFiles: KIM_MAX_NUMBER_OF_FILES,
    onDropRejected: (rejections) => {
      for (const rejection of rejections) {
        for (const error of rejection.errors) {
          if (error.code === 'file-too-large') {
            toast.error(`File is too large. Maximum size is ${KIM_MAX_FILE_SIZE / 1024 / 1024}MB.`);
          } else if (error.code === 'too-many-files') {
            toast.error(`Too many files. Maximum is ${KIM_MAX_NUMBER_OF_FILES} files.`);
          } else {
            toast.error(error.message);
          }
        }
      }
    },
  });

  if (!matterId || !subsetId) return null;

  const [hasUnfilledPlaceholders, setHasUnfilledPlaceholders] = useState(false);
  const isSubmitDisabled = disabled || isUploading || hasUnfilledPlaceholders;

  return (
    <div
      // {...getRootProps()}
      className={cn('relative w-full', isDragActive && 'rounded-lg ring-2 ring-blue-400 ring-offset-1')}
    >
      {/* <input {...getInputProps()} />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) onFilesAdded?.(files);
          e.target.value = '';
        }}
      /> */}

      <div
        ref={containerRef}
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'w-full cursor-text rounded-xl border bg-white text-sm ring-offset-2 ring-offset-transparent transition-colors duration-150 focus-within:ring-1 focus-within:ring-teal-500 focus-within:ring-offset-white',
          disabled && 'border-gray-200/60 bg-[#fcfcfc]',
        )}
      >
        <div className="mx-2">
          <KimUploadedFilesList uploadedFiles={uploadedFiles} isUploading={isUploading} onFileRemove={onFileRemove} />
        </div>

        <DocMentionsInput
          ref={inputRef}
          value={value}
          onChange={onChange}
          onSubmit={onSubmit}
          onPlaceholdersChange={setHasUnfilledPlaceholders}
          matterId={matterId}
          subsetId={subsetId}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          autoFocus={autoFocus}
          popoverAnchorRef={containerRef}
        />

        <div className={cn('flex items-end justify-end p-2 pt-0', disabled && 'opacity-50')}>
          {/* {onFilesAdded && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={disabled || isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="size-8 rounded-full text-gray-400 hover:text-gray-600"
                >
                  <Icon className="lucide--paperclip" />
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={2}>Attach files</TooltipContent>
            </Tooltip>
          )} */}

          <Button disabled={isSubmitDisabled} onClick={onSubmit} variant="primary" className="m-1 size-8 rounded-full">
            <Icon className="lucide--arrow-right" />
          </Button>
        </div>
      </div>
    </div>
  );
});
